'use client';

import {
  type ButtonProps,
  Button as DsButton,
} from '@ironhack/design-system2/components/button';
import { type LinkProps } from '@ironhack/design-system2/components/link';
import { type IconProps } from '@ironhack/design-system2/icons';
import { always, isEmpty, omit, pick, pipe, propOr, when } from 'ramda';
import { type ComponentType, type ReactElement } from 'react';

import { Link } from '@/app/components/client/link';
import { getIconAsset } from '@/app/components/server/icon';
import { type DatoIconBlock } from '@/lib/datocms/types';
import { getButtonIcon, getButtonTarget } from './button-target';
import { type DatoButtonLikeBlock } from '../types';
import type { WithTracking } from '@/types';

type IconBlock = DatoIconBlock | null | undefined;

type Placement = 'left' | 'right';

type ButtonIconProps = Record<`${string}Icon`, ComponentType<IconProps>>;

export const getButtonIconProps = (
  iconBlock: IconBlock
): ButtonIconProps | null => {
  if (!iconBlock) return null;

  const iconPlacement = pipe(
    propOr('right', 'placement'),
    when(isEmpty, always('right')) as (p: Placement | '') => Placement
  )(iconBlock);

  const IconAsset = getIconAsset(iconBlock);
  return { [`${iconPlacement}Icon`]: IconAsset };
};

const layoutPropNames = [
  'w',
  'h',
  'minW',
  'maxW',
  'minH',
  'maxH',
  'display',
  'gap',
  'rowGap',
  'columnGap',
  'alignItems',
  'align',
  'alignContent',
  'justifyItems',
  'justifyContent',
  'justify',
  'flexWrap',
  'wrap',
  'flexDirection',
  'flexDir',
  'direction',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'justifySelf',
  'alignSelf',
  'order',
] as const;

type Props = WithTracking<DatoButtonLikeBlock> &
  Pick<LinkProps, 'rel'> &
  ButtonProps;

export const Button = (props: Props): ReactElement => {
  const {
    id,
    rel,
    text,
    tracking = {},
    ...buttonProps
  } = omit(
    [
      '__typename',
      'anchor',
      'blogArticle',
      'gaEvent',
      'icon',
      'iconColor',
      'iconName',
      'iconPlacement',
      'io',
      'link',
      'route',
      'target',
    ],
    props
  );

  const target = getButtonTarget(props);
  const iconProps = getButtonIconProps(getButtonIcon(props));
  const layoutProps = pick(layoutPropNames, buttonProps);
  const finalButtonProps = { ...buttonProps, ...iconProps };

  return (
    <Link
      {...target}
      // Prevent Dato ids from showing up in the HTML
      {...(Number.isNaN(Number(id)) && { id })}
      rel={rel}
      tracking={tracking}
      withoutDecoration
      {...layoutProps}
    >
      <DsButton {...finalButtonProps}>{text}</DsButton>
    </Link>
  );
};
